/**
 * MapBaseComponent — rewritten with Mapbox GL.
 *
 * Keeps the same Props interface as the original Leaflet version so
 * TraceabilityPage, CropsPage, CropDetailModal, etc. continue working
 * without import changes. Internally uses a single Mapbox GL map instance.
 */

import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-csp';
import 'mapbox-gl/dist/mapbox-gl.css';

import { getMapboxToken, validateMapboxConfig, BASEMAP_STYLES, RWANDA_CENTER, RWANDA_ZOOM } from '../../features/maps/config/mapboxConfig';
import type { GeoCoord } from '../../features/maps/types/mapTypes';

// ─── Types (same as original) ──────────────────────────────────────

interface Coordinates {
    lat: number;
    lng: number;
}

interface FarmerPlot {
    id: number;
    name: string;
    plot_code?: string;
    household_type: string;
    plot_boundary_coordinates: Coordinates[];
    cohort_name: string;
    cohort_id: number;
    plot_area_hectares?: number;
}

interface Cohort {
    id: number;
    name: string;
    cropping_system?: string;
    boundary_coordinates: Coordinates[];
    boundary_color?: string;
    farmer_count: number | string;
    total_area_hectares?: number | string;
}

interface Warehouse {
    id: number;
    name: string;
    type: string;
    location_lat: number;
    location_lng: number;
    location_name: string;
    capacity_kg: number;
    current_usage_kg: number;
    temperature_celsius: number | null;
    status: string;
    usage_percentage: number;
}

interface TreePoint {
    id: number;
    tree_code: string;
    crop_type: string;
    variety?: string;
    farmer_name?: string;
    cohort_name?: string;
    latitude: number;
    longitude: number;
    health_status: string;
    tree_status: string;
    open_issue_count?: number;
    latest_issue_photo?: string;
    latest_tree_photo?: string;
}

type RwandaBoundaryLevel = 'none' | 'provinces' | 'districts' | 'sectors' | 'cells';

interface Props {
    farmers: FarmerPlot[];
    fieldPlots?: FarmerPlot[];
    cohorts: Cohort[];
    warehouses: Warehouse[];
    trees?: TreePoint[];
    center: Coordinates;
    zoom: number;
    baseLayer?: 'standard' | 'satellite' | 'terrain';
    onFarmerClick?: (farmer: FarmerPlot) => void;
    onFieldPlotClick?: (field: FarmerPlot) => void;
    onWarehouseClick?: (warehouse: Warehouse) => void;
    onCohortClick?: (cohort: Cohort) => void;
    onTreeClick?: (tree: TreePoint) => void;
    onMapReady?: (map: any) => void;
    sizeToken?: string | number;
    adminBoundaryLevel?: RwandaBoundaryLevel;
    showLocationTracker?: boolean;
    focusedTreeId?: number | null;
    sketchMode?: boolean;
    draftBoundary?: Coordinates[];
    onSketchPoint?: (point: Coordinates) => void;
    onRemoveSketchPoint?: (index: number) => void;
    navigateRequested?: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────

const toLngLat = (c: Coordinates): [number, number] => [c.lng, c.lat];

const householdTypeColor = (type: string) => {
    const t = String(type || '').toLowerCase();
    if (t === 'cooperative') return '#3B82F6';
    if (t === 'vsla') return '#8B5CF6';
    if (t === 'farmer_group') return '#F59E0B';
    return '#10B981';
};

const cohortColors = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4'];
const cohortColor = (id: number) => cohortColors[id % cohortColors.length];

const treeHealthColor = (status: string) => {
    const s = String(status || '').toLowerCase();
    if (s === 'critical') return '#DC2626';
    if (s === 'at_risk') return '#F97316';
    if (s === 'watch' || s === 'needs_inspection') return '#D97706';
    if (s === 'excellent') return '#059669';
    return '#16A34A';
};

// Source/layer IDs
const SOURCES = {
    farmers: 'mbc-farmers',
    fields: 'mbc-fields',
    cohorts: 'mbc-cohorts',
    cohortsCentroid: 'mbc-cohorts-centroid',
    warehouses: 'mbc-warehouses',
    trees: 'mbc-trees',
    draft: 'mbc-draft',
} as const;

const LAYERS = {
    farmersFill: 'mbc-farmers-fill',
    farmersLine: 'mbc-farmers-line',
    fieldsFill: 'mbc-fields-fill',
    fieldsLine: 'mbc-fields-line',
    cohortsFill: 'mbc-cohorts-fill',
    cohortsLine: 'mbc-cohorts-line',
    cohortsCentroid: 'mbc-cohorts-centroid',
    warehousesCircle: 'mbc-warehouses-circle',
    warehousesLabel: 'mbc-warehouses-label',
    treesCluster: 'mbc-trees-cluster',
    treesClusterCount: 'mbc-trees-cluster-count',
    treesPoint: 'mbc-trees-point',
    draftLine: 'mbc-draft-line',
    draftFill: 'mbc-draft-fill',
    draftPoint: 'mbc-draft-point',
} as const;

// ─── GeoJSON builders ───────────────────────────────────────────────

function polygonFeature(id: string | number, coords: Coordinates[], props: Record<string, unknown> = {}) {
    return {
        type: 'Feature' as const,
        id,
        geometry: {
            type: 'Polygon' as const,
            coordinates: [coords.map(c => toLngLat(c))],
        },
        properties: props,
    };
}

function pointFeature(id: string | number, coord: Coordinates, props: Record<string, unknown> = {}) {
    return {
        type: 'Feature' as const,
        id,
        geometry: {
            type: 'Point' as const,
            coordinates: toLngLat(coord),
        },
        properties: props,
    };
}

function fc(features: any[]) {
    return { type: 'FeatureCollection' as const, features };
}

// ─── Admin boundary cache ───────────────────────────────────────────

const BOUNDARY_FILES: Record<string, string> = {
    provinces: '/geodata/rwanda/rwanda_provinces.geojson',
    districts: '/geodata/rwanda/rwanda_districts.geojson',
    sectors: '/geodata/rwanda/rwanda_sectors.geojson',
    cells: '/geodata/rwanda/rwanda_cells.geojson',
};

const boundaryCache = new Map<string, any>();
const MIN_ZOOM: Record<string, number> = { provinces: 6, districts: 7, sectors: 10, cells: 12 };

// ─── Component ──────────────────────────────────────────────────────

const MapBaseComponent: React.FC<Props> = ({
    farmers,
    fieldPlots = [],
    cohorts,
    warehouses,
    trees = [],
    center,
    zoom,
    baseLayer = 'standard',
    onFarmerClick,
    onFieldPlotClick,
    onWarehouseClick,
    onCohortClick,
    onTreeClick,
    onMapReady,
    sizeToken,
    adminBoundaryLevel = 'none',
    showLocationTracker = true,
    focusedTreeId = null,
    sketchMode = false,
    draftBoundary = [],
    onSketchPoint,
    onRemoveSketchPoint,
    navigateRequested = false,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const navigatingRef = useRef(false);
    const initialStyleKey = baseLayer === 'standard' ? 'streets' : baseLayer === 'satellite' ? 'satellite' : 'terrain';
    const appliedStyleRef = useRef(BASEMAP_STYLES[initialStyleKey]);
    const callbacksRef = useRef({ onFarmerClick, onFieldPlotClick, onWarehouseClick, onCohortClick, onTreeClick, onSketchPoint, onRemoveSketchPoint, onMapReady });
    callbacksRef.current = { onFarmerClick, onFieldPlotClick, onWarehouseClick, onCohortClick, onTreeClick, onSketchPoint, onRemoveSketchPoint, onMapReady };

    // ── Initialize map ─────────────────────────────────────
    useEffect(() => {
        if (!containerRef.current) return;

        const config = validateMapboxConfig();
        if (!config.valid) {
            console.error('[MapBaseComponent]', config.error);
            return;
        }

        mapboxgl.accessToken = getMapboxToken();
        mapboxgl.workerUrl = `${process.env.PUBLIC_URL || ''}/mapbox-gl-csp-worker.js`;
        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: BASEMAP_STYLES[baseLayer === 'standard' ? 'streets' : baseLayer === 'satellite' ? 'satellite' : 'terrain'],
            center: toLngLat(center),
            zoom,
            maxZoom: 22,
            attributionControl: true,
        });

        map.addControl(new mapboxgl.ScaleControl({ unit: 'metric' }), 'bottom-left');
        map.addControl(new mapboxgl.NavigationControl({ showCompass: true }), 'bottom-right');

        // Location tracker button
        if (showLocationTracker) {
            const btn = document.createElement('button');
            btn.className = 'mapboxgl-ctrl-icon';
            btn.style.cssText = 'width:30px;height:30px;font-size:16px;font-weight:900;color:#047857;background:#fff;cursor:pointer;border:none;border-radius:4px;';
            btn.textContent = '◎';
            btn.title = 'Find my location';
            btn.onclick = () => {
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        map.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 17 });
                        // Add accuracy circle + marker
                        new mapboxgl.Marker({ color: '#10B981' })
                            .setLngLat([pos.coords.longitude, pos.coords.latitude])
                            .setPopup(new mapboxgl.Popup().setText(`Accuracy: ${Math.round(pos.coords.accuracy)}m`))
                            .addTo(map);
                    },
                    () => { /* ignore */ },
                    { enableHighAccuracy: true },
                );
            };
            const nav = map.getContainer().querySelector('.mapboxgl-ctrl-top-right');
            if (nav) {
                const wrapper = document.createElement('div');
                wrapper.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
                wrapper.appendChild(btn);
                nav.appendChild(wrapper);
            }
        }

        // Click handler for sketch mode
        map.on('click', (e) => {
            if (sketchMode && callbacksRef.current.onSketchPoint) {
                callbacksRef.current.onSketchPoint({ lat: e.lngLat.lat, lng: e.lngLat.lng });
            }
        });

        // Cursor for sketch mode
        if (sketchMode) {
            map.getCanvas().style.cursor = 'crosshair';
        }

        map.on('load', () => {
            // Add all sources and layers
            addAllLayers(map);

            // Hover cursors
            const hoverLayers = [
                LAYERS.farmersFill, LAYERS.fieldsFill, LAYERS.cohortsFill,
                LAYERS.warehousesCircle, LAYERS.treesPoint, LAYERS.treesCluster,
            ];
            for (const lid of hoverLayers) {
                if (map.getLayer(lid)) {
                    map.on('mouseenter', lid, () => { map.getCanvas().style.cursor = 'pointer'; });
                    map.on('mouseleave', lid, () => { map.getCanvas().style.cursor = sketchMode ? 'crosshair' : ''; });
                }
            }

            callbacksRef.current.onMapReady?.(map);
        });

        mapRef.current = map;

        return () => {
            map.remove();
            mapRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Add all source/layer pairs ─────────────────────────
    const addAllLayers = useCallback((map: mapboxgl.Map) => {
        // Farmers
        if (!map.getSource(SOURCES.farmers)) {
            map.addSource(SOURCES.farmers, { type: 'geojson', data: fc([]) });
            map.addLayer({ id: LAYERS.farmersFill, type: 'fill', source: SOURCES.farmers, paint: { 'fill-color': ['get', '_color'], 'fill-opacity': 0.35 }, filter: ['==', ['get', '_type'], 'farmer'] });
            map.addLayer({ id: LAYERS.farmersLine, type: 'line', source: SOURCES.farmers, paint: { 'line-color': ['get', '_color'], 'line-width': 1.5, 'line-dasharray': [4, 2] }, filter: ['==', ['get', '_type'], 'farmer'] });
        }

        // Fields
        if (!map.getSource(SOURCES.fields)) {
            map.addSource(SOURCES.fields, { type: 'geojson', data: fc([]) });
            map.addLayer({ id: LAYERS.fieldsFill, type: 'fill', source: SOURCES.fields, paint: { 'fill-color': '#FACC15', 'fill-opacity': 0.15 }, filter: ['==', ['get', '_type'], 'field'] });
            map.addLayer({ id: LAYERS.fieldsLine, type: 'line', source: SOURCES.fields, paint: { 'line-color': '#FACC15', 'line-width': 3, 'line-dasharray': [8, 6] }, filter: ['==', ['get', '_type'], 'field'] });
        }

        // Cohorts
        if (!map.getSource(SOURCES.cohorts)) {
            map.addSource(SOURCES.cohorts, { type: 'geojson', data: fc([]) });
            map.addLayer({ id: LAYERS.cohortsFill, type: 'fill', source: SOURCES.cohorts, paint: { 'fill-color': ['get', '_color'], 'fill-opacity': 0.1 }, filter: ['==', ['get', '_type'], 'cohort'] });
            map.addLayer({ id: LAYERS.cohortsLine, type: 'line', source: SOURCES.cohorts, paint: { 'line-color': ['get', '_color'], 'line-width': 2.5 }, filter: ['==', ['get', '_type'], 'cohort'] });
        }

        if (!map.getSource(SOURCES.cohortsCentroid)) {
            map.addSource(SOURCES.cohortsCentroid, { type: 'geojson', data: fc([]) });
            map.addLayer({ id: LAYERS.cohortsCentroid, type: 'circle', source: SOURCES.cohortsCentroid, paint: { 'circle-color': ['get', '_color'], 'circle-radius': 8, 'circle-opacity': 0.6, 'circle-stroke-width': 1, 'circle-stroke-color': '#fff' } });
        }

        // Warehouses
        if (!map.getSource(SOURCES.warehouses)) {
            map.addSource(SOURCES.warehouses, { type: 'geojson', data: fc([]) });
            map.addLayer({ id: LAYERS.warehousesCircle, type: 'circle', source: SOURCES.warehouses, paint: { 'circle-color': '#7C3AED', 'circle-radius': 10, 'circle-stroke-width': 3, 'circle-stroke-color': '#fff' } });
            map.addLayer({ id: LAYERS.warehousesLabel, type: 'symbol', source: SOURCES.warehouses, layout: { 'text-field': ['get', 'name'], 'text-size': 11, 'text-offset': [0, 1.5] }, paint: { 'text-color': '#374151', 'text-halo-color': '#fff', 'text-halo-width': 1 } });
        }

        // Trees (with clustering)
        if (!map.getSource(SOURCES.trees)) {
            map.addSource(SOURCES.trees, { type: 'geojson', data: fc([]), cluster: true, clusterMaxZoom: 14, clusterRadius: 50 });
            map.addLayer({ id: LAYERS.treesCluster, type: 'circle', source: SOURCES.trees, filter: ['has', 'point_count'], paint: { 'circle-color': '#BBF7D0', 'circle-radius': ['step', ['get', 'point_count'], 18, 100, 24, 500, 30], 'circle-stroke-width': 2, 'circle-stroke-color': '#fff' } });
            map.addLayer({ id: LAYERS.treesClusterCount, type: 'symbol', source: SOURCES.trees, filter: ['has', 'point_count'], layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-size': 12 }, paint: { 'text-color': '#166534' } });
            map.addLayer({ id: LAYERS.treesPoint, type: 'circle', source: SOURCES.trees, filter: ['!', ['has', 'point_count']], paint: { 'circle-color': ['case', ['>', ['coalesce', ['get', '_openIssues'], 0], 0], '#DC2626', ['coalesce', ['get', '_healthColor'], '#16A34A']], 'circle-radius': 4.5, 'circle-stroke-width': 1.5, 'circle-stroke-color': '#fff' } });
        }

        // Draft boundary
        if (!map.getSource(SOURCES.draft)) {
            map.addSource(SOURCES.draft, { type: 'geojson', data: fc([]) });
            map.addLayer({ id: LAYERS.draftFill, type: 'fill', source: SOURCES.draft, paint: { 'fill-color': '#10B981', 'fill-opacity': 0.15 }, filter: ['==', ['get', '_type'], 'draft-poly'] });
            map.addLayer({ id: LAYERS.draftLine, type: 'line', source: SOURCES.draft, paint: { 'line-color': '#059669', 'line-width': 3, 'line-dasharray': [6, 4] }, filter: ['==', ['get', '_type'], 'draft-line'] });
            map.addLayer({ id: LAYERS.draftPoint, type: 'circle', source: SOURCES.draft, paint: { 'circle-color': ['case', ['get', '_isFirst'], '#DC2626', '#064E3B'], 'circle-radius': 7, 'circle-stroke-width': 3, 'circle-stroke-color': '#fff' }, filter: ['==', ['get', '_type'], 'draft-point'] });
        }
    }, []);

    // ── Update data on prop changes ─────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !map.isStyleLoaded()) return;

        // Farmers
        const fSrc = map.getSource(SOURCES.farmers);
        if (fSrc?.type === 'geojson') {
            const feats = farmers
                .filter(f => (f.plot_boundary_coordinates?.length || 0) >= 3)
                .map(f => {
                    const feat = polygonFeature(f.id, f.plot_boundary_coordinates, {
                        _type: 'farmer',
                        _color: householdTypeColor(f.household_type),
                        name: f.name,
                    });
                    return feat;
                });
            (fSrc as any).setData(fc(feats));
        }

        // Fields
        const flSrc = map.getSource(SOURCES.fields);
        if (flSrc?.type === 'geojson') {
            const feats = fieldPlots
                .filter(f => (f.plot_boundary_coordinates?.length || 0) >= 3)
                .map(f => polygonFeature(`field-${f.id}`, f.plot_boundary_coordinates, { _type: 'field', name: f.name || f.plot_code }));
            (flSrc as any).setData(fc(feats));
        }

        // Cohorts
        const cSrc = map.getSource(SOURCES.cohorts);
        if (cSrc?.type === 'geojson') {
            const feats = cohorts
                .filter(c => (c.boundary_coordinates?.length || 0) >= 3)
                .map((c, i) => polygonFeature(c.id, c.boundary_coordinates, { _type: 'cohort', _color: c.boundary_color || cohortColor(c.id), name: c.name }));
            (cSrc as any).setData(fc(feats));
        }

        // Cohort centroids
        const ccSrc = map.getSource(SOURCES.cohortsCentroid);
        if (ccSrc?.type === 'geojson') {
            const feats = cohorts
                .filter(c => (c.boundary_coordinates?.length || 0) >= 3)
                .map((c, i) => {
                    const sum = c.boundary_coordinates.reduce((a, p) => [a[0] + p.lat, a[1] + p.lng], [0, 0]);
                    const n = c.boundary_coordinates.length;
                    return pointFeature(`cc-${c.id}`, { lat: sum[0] / n, lng: sum[1] / n }, { _color: c.boundary_color || cohortColor(c.id), name: c.name });
                });
            (ccSrc as any).setData(fc(feats));
        }

        // Warehouses
        const wSrc = map.getSource(SOURCES.warehouses);
        if (wSrc?.type === 'geojson') {
            const feats = warehouses.map(w => pointFeature(w.id, { lat: w.location_lat, lng: w.location_lng }, { name: w.name }));
            (wSrc as any).setData(fc(feats));
        }

        // Trees
        const tSrc = map.getSource(SOURCES.trees);
        if (tSrc?.type === 'geojson') {
            const feats = trees
                .filter(t => !isNaN(Number(t.latitude)) && !isNaN(Number(t.longitude)))
                .map(t => {
                    const feat = pointFeature(t.id, { lat: Number(t.latitude), lng: Number(t.longitude) }, {
                        treeCode: t.tree_code,
                        cropType: t.crop_type,
                        farmerName: t.farmer_name || '',
                        _healthColor: treeHealthColor(t.health_status || t.tree_status),
                        _openIssues: Number(t.open_issue_count || 0),
                    });
                    return feat;
                });
            (tSrc as any).setData(fc(feats));
        }
    }, [farmers, fieldPlots, cohorts, warehouses, trees]);

    // ── Draft boundary ─────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !map.isStyleLoaded()) return;
        const dSrc = map.getSource(SOURCES.draft);
        if (!dSrc || dSrc.type !== 'geojson') return;

        const features: any[] = [];

        if (draftBoundary.length >= 3) {
            // Closed polygon
            features.push(polygonFeature('draft-poly', [...draftBoundary, draftBoundary[0]], { _type: 'draft-poly' }));
            // Line
            features.push({
                type: 'Feature', id: 'draft-line',
                geometry: { type: 'LineString', coordinates: [...draftBoundary, draftBoundary[0]].map(c => toLngLat(c)) },
                properties: { _type: 'draft-line' },
            });
        } else if (draftBoundary.length >= 2) {
            features.push({
                type: 'Feature', id: 'draft-line',
                geometry: { type: 'LineString', coordinates: draftBoundary.map(c => toLngLat(c)) },
                properties: { _type: 'draft-line' },
            });
        }

        // Points
        draftBoundary.forEach((pt, i) => {
            features.push(pointFeature(`draft-pt-${i}`, pt, { _type: 'draft-point', _isFirst: i === 0 }));
        });

        (dSrc as any).setData(fc(features));
    }, [draftBoundary]);

    // ── Navigate on explicit request ────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !navigateRequested) return;
        navigatingRef.current = true;
        map.flyTo({ center: toLngLat(center), zoom, duration: 500, essential: true });
    }, [navigateRequested, center.lat, center.lng, zoom]);

    // ── Basemap style ──────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const styleKey = baseLayer === 'standard' ? 'streets' : baseLayer === 'satellite' ? 'satellite' : 'terrain';
        const targetStyle = BASEMAP_STYLES[styleKey];
        if (appliedStyleRef.current === targetStyle) return;
        map.setStyle(targetStyle);
        appliedStyleRef.current = targetStyle;
        map.once('style.load', () => {
            const m = mapRef.current;
            if (m?.isStyleLoaded()) addAllLayers(m);
        });
    }, [baseLayer, addAllLayers]);

    // ── Focus tree ─────────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map || focusedTreeId == null) return;
        const tree = trees.find(t => t.id === focusedTreeId);
        if (!tree) return;
        const lat = Number(tree.latitude);
        const lng = Number(tree.longitude);
        if (isNaN(lat) || isNaN(lng)) return;
        map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 20), duration: 300 });
    }, [focusedTreeId, trees]);

    // ── Click handlers ─────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const handleClick = (layerId: string, items: any[], idKey: string, callback?: (item: any) => void) => {
            if (!map.getLayer(layerId)) return;
            map.on('click', layerId, (e) => {
                if (!e.features?.length || sketchMode) return;
                const featureId = e.features[0].id;
                const item = items.find((i: any) => String(i[idKey]) === String(featureId));
                if (item) callback?.(item);
            });
        };

        // Farmers
        const farmerHandler = (e: mapboxgl.MapMouseEvent) => {
            if (!e.features?.length || sketchMode) return;
            const id = e.features[0].id;
            const farmer = farmers.find(f => String(f.id) === String(id));
            if (farmer) callbacksRef.current.onFarmerClick?.(farmer);
        };
        if (map.getLayer(LAYERS.farmersFill)) map.on('click', LAYERS.farmersFill, farmerHandler);

        // Fields
        const fieldHandler = (e: mapboxgl.MapMouseEvent) => {
            if (!e.features?.length || sketchMode) return;
            const id = Number(String(e.features[0].id).replace('field-', ''));
            const field = fieldPlots.find(f => f.id === id);
            if (field) callbacksRef.current.onFieldPlotClick?.(field);
        };
        if (map.getLayer(LAYERS.fieldsFill)) map.on('click', LAYERS.fieldsFill, fieldHandler);

        // Cohorts
        const cohortHandler = (e: mapboxgl.MapMouseEvent) => {
            if (!e.features?.length || sketchMode) return;
            const id = e.features[0].id;
            const cohort = cohorts.find(c => String(c.id) === String(id));
            if (cohort) callbacksRef.current.onCohortClick?.(cohort);
        };
        if (map.getLayer(LAYERS.cohortsFill)) map.on('click', LAYERS.cohortsFill, cohortHandler);

        // Warehouses
        const warehouseHandler = (e: mapboxgl.MapMouseEvent) => {
            if (!e.features?.length || sketchMode) return;
            const id = e.features[0].id;
            const warehouse = warehouses.find(w => String(w.id) === String(id));
            if (warehouse) callbacksRef.current.onWarehouseClick?.(warehouse);
        };
        if (map.getLayer(LAYERS.warehousesCircle)) map.on('click', LAYERS.warehousesCircle, warehouseHandler);

        // Trees
        const treeHandler = (e: mapboxgl.MapMouseEvent) => {
            if (!e.features?.length || sketchMode) return;
            const id = e.features[0].id;
            const tree = trees.find(t => t.id === id);
            if (tree) callbacksRef.current.onTreeClick?.(tree);
        };
        if (map.getLayer(LAYERS.treesPoint)) map.on('click', LAYERS.treesPoint, treeHandler);

        // Clusters — zoom in
        const clusterHandler = (e: mapboxgl.MapMouseEvent) => {
            if (!e.features?.length) return;
            const clusterId = e.features[0].properties?.cluster_id;
            if (clusterId != null) {
                const src = map.getSource(SOURCES.trees);
                if (src?.type === 'geojson') {
                    (src as any).getClusterLeaves(clusterId, 1000, 0, (err: any, features: any[]) => {
                        if (err || !features?.length) return;
                        let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
                        for (const f of features) {
                            const [lng, lat] = f.geometry.coordinates;
                            if (lng < minLng) minLng = lng;
                            if (lng > maxLng) maxLng = lng;
                            if (lat < minLat) minLat = lat;
                            if (lat > maxLat) maxLat = lat;
                        }
                        map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 50, maxZoom: 16 });
                    });
                }
            }
        };
        if (map.getLayer(LAYERS.treesCluster)) map.on('click', LAYERS.treesCluster, clusterHandler);

        return () => {
            map.off('click', LAYERS.farmersFill, farmerHandler);
            map.off('click', LAYERS.fieldsFill, fieldHandler);
            map.off('click', LAYERS.cohortsFill, cohortHandler);
            map.off('click', LAYERS.warehousesCircle, warehouseHandler);
            map.off('click', LAYERS.treesPoint, treeHandler);
            map.off('click', LAYERS.treesCluster, clusterHandler);
        };
    }, [farmers, fieldPlots, cohorts, warehouses, trees, sketchMode]);

    // ── Sketch mode cursor ─────────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        map.getCanvas().style.cursor = sketchMode ? 'crosshair' : '';
    }, [sketchMode]);

    // ── Render ─────────────────────────────────────────────
    return (
        <div
            ref={containerRef}
            style={{ height: '100%', width: '100%', borderRadius: '0.75rem' }}
        />
    );
};

export default MapBaseComponent;
